import {createApp} from 'vue';
import {connectVue} from '@/modules/configureBugsnag';
import configureGlobalComponents from '../modules/configureGlobalComponents';
import axiosClient from '../modules/axiosClient';
import useVuelidate from '@vuelidate/core';
import {
    createBaseLeasingSearchCriteria,
    setManufacturer,
    setStringArrayFacet,
} from '@/modules/leasingSearchCriteriaBuilder';

declare interface Models {
    [key: string]: string,
}

type DropdownOption = {value: string, text: string};

const app = createApp({
    data() {
        return {
            searchType: 'cash',
            carModels: {} as Models,
            make: '',
            range: '',
            rangeOptions: [] as DropdownOption[],
            model: '',
            loadingRanges: false,
            loadingModels: false,
            postcode: '',
            minprice: '',
            maxprice: '',
            minmonthly: '',
            maxmonthly: '',

            priceOptions: [
                {value: '50', text: '50'},
                {value: '75', text: '75'},
                {value: '100', text: '100'},
                {value: '125', text: '125'},
                {value: '150', text: '150'},
                {value: '175', text: '175'},
                {value: '200', text: '200'},
                {value: '225', text: '225'},
                {value: '250', text: '250'},
                {value: '275', text: '275'},
                {value: '300', text: '300'},
                {value: '325', text: '325'},
                {value: '350', text: '350'},
                {value: '375', text: '375'},
                {value: '400', text: '400'},
                {value: '425', text: '425'},
                {value: '450', text: '450'},
                {value: '475', text: '475'},
                {value: '500', text: '500'},
                {value: '550', text: '550'},
                {value: '600', text: '600'},
                {value: '650', text: '650'},
                {value: '700', text: '700'},
                {value: '750', text: '750'},
                {value: '800', text: '800'},
                {value: '900', text: '900'},
                {value: '1000', text: '1,000'},
                {value: '1100', text: '1,100'},
                {value: '1200', text: '1,200'},
                {value: '1300', text: '1,300'},
                {value: '1400', text: '1,400'},
                {value: '1500', text: '1,500'},
                {value: '1750', text: '1,750'},
                {value: '2000', text: '2,000'},
                {value: '2250', text: '2,250'},
                {value: '2750', text: '2,750'},
                {value: '3000', text: '3,000'},
                {value: '3500', text: '3,500'},
                {value: '4000', text: '4,000'},
                {value: '4500', text: '4,500'},
                {value: '5000', text: '5,000'},
            ],
        };
    },
    props: [
        'version',
    ],
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    validations() {
        return {};
    },
    watch: {
        make(newValue: string) {
            this.range = '';
            this.rangeOptions = [];
            this.model = '';
            this.carModels = {};
            this.loadingRanges = false;
            this.loadingModels = false;

            if (!newValue) {
                return;
            }

            this.fetchRangeOptions();
        },
        range(newValue: string) {
            this.model = '';
            this.carModels = {};
            this.loadingModels = false;

            if (!newValue || !this.make) {
                return;
            }

            this.fetchModelOptions();
        },
        minprice(newValue: string, oldValue: string) {
            if (newValue === oldValue) {
                return;
            }

            this.syncMaxPriceWithMin();
        },
        maxprice(newValue: string, oldValue: string) {
            if (newValue === oldValue) {
                return;
            }

            this.syncMinPriceWithMax();
        },
    },
    computed: {
        vehicleModels(): DropdownOption[] {
            const dropdown = [] as DropdownOption[];
            Object.keys(this.carModels).forEach((index) => {
                const model = this.carModels[index].toUpperCase();
                dropdown.push({value: index, text: model});
            });
            return dropdown;
        },
        filteredMinPriceOptions(): DropdownOption[] {
            if (!this.maxprice) {
                return this.priceOptions;
            }

            const maxValue = this.parseOptionNumber(this.maxprice);
            if (maxValue === null) {
                return this.priceOptions;
            }

            return this.priceOptions.filter((option: DropdownOption) => {
                const optionValue = this.parseOptionNumber(option.value);
                return optionValue !== null && optionValue < maxValue;
            });
        },
        filteredMaxPriceOptions(): DropdownOption[] {
            if (!this.minprice) {
                return this.priceOptions;
            }

            const minValue = this.parseOptionNumber(this.minprice);
            if (minValue === null) {
                return this.priceOptions;
            }

            return this.priceOptions.filter((option: DropdownOption) => {
                const optionValue = this.parseOptionNumber(option.value);
                return optionValue !== null && optionValue > minValue;
            });
        },
        /**
         * @return {boolean}
         */
        isMinGreaterThanMax(): boolean {
            if (this.searchType === 'cash') {
                return parseInt(this.minprice) > parseInt(this.maxprice) && parseInt(this.maxprice) != -1;
            }

            return parseInt(this.minmonthly) > parseInt(this.maxmonthly) && parseInt(this.maxmonthly) != -1;
        },
    },
    methods: {
        mapFacetStringsToOptions(values: string[]): DropdownOption[] {
            return values.map((raw) => {
                const value = String(raw);
                return {value, text: value.toUpperCase()};
            });
        },
        parseOptionNumber(value: string): number|null {
            const parsed = Number.parseInt(value, 10);
            return Number.isNaN(parsed) ? null : parsed;
        },
        getNearestOptionValue(targetValue: string, options: DropdownOption[]): string {
            if (!options.length) {
                return '';
            }

            const target = this.parseOptionNumber(targetValue);
            if (target === null) {
                return '';
            }

            let nearestValue = options[0].value;
            let nearestDiff = Math.abs((this.parseOptionNumber(nearestValue) ?? target) - target);

            options.forEach((option: DropdownOption) => {
                const optionNumericValue = this.parseOptionNumber(option.value);
                if (optionNumericValue === null) {
                    return;
                }

                const diff = Math.abs(optionNumericValue - target);
                if (diff < nearestDiff) {
                    nearestDiff = diff;
                    nearestValue = option.value;
                }
            });

            return nearestValue;
        },
        syncMaxPriceWithMin(): void {
            if (!this.maxprice) {
                return;
            }

            const currentValueIsAllowed = this.filteredMaxPriceOptions.some((option: DropdownOption) => option.value === this.maxprice);
            if (currentValueIsAllowed) {
                return;
            }

            const nearestAllowedValue = this.getNearestOptionValue(this.maxprice, this.filteredMaxPriceOptions);
            if (nearestAllowedValue !== this.maxprice) {
                this.maxprice = nearestAllowedValue;
            }
        },
        syncMinPriceWithMax(): void {
            if (!this.minprice) {
                return;
            }

            const currentValueIsAllowed = this.filteredMinPriceOptions.some((option: DropdownOption) => option.value === this.minprice);
            if (currentValueIsAllowed) {
                return;
            }

            const nearestAllowedValue = this.getNearestOptionValue(this.minprice, this.filteredMinPriceOptions);
            if (nearestAllowedValue !== this.minprice) {
                this.minprice = nearestAllowedValue;
            }
        },

        fetchRangeOptions(): void {
            this.loadingRanges = true;
            this.rangeOptions = [];

            const searchCriteria = createBaseLeasingSearchCriteria();
            setManufacturer(searchCriteria, this.make);

            const payload = {
                facetName: 'Range',
                searchCriteria,
            };

            axiosClient.post('/api/leasing/search-facet', payload).then((response) => {
                const data = response.data;
                const list = Array.isArray(data) ? data : Object.values(data) as string[];
                this.rangeOptions = this.mapFacetStringsToOptions(list.map(String));
                this.loadingRanges = false;
            }, () => {
                this.rangeOptions = [];
                this.loadingRanges = false;
            });
        },

        fetchModelOptions(): void {
            this.loadingModels = true;
            this.carModels = {};

            const searchCriteria = createBaseLeasingSearchCriteria();
            setManufacturer(searchCriteria, this.make);
            setStringArrayFacet(searchCriteria, 'range', [this.range]);

            const payload = {
                facetName: 'Model',
                searchCriteria,
            };

            axiosClient.post('/api/leasing/search-facet', payload).then((response) => {
                const data = response.data;
                const options = Array.isArray(data) ? data : Object.values(data);
                this.carModels = options.reduce((models: Models, value: string) => {
                    const modelValue = String(value);
                    const key = modelValue.toLowerCase().trim().replace(/\s+/g, '-');
                    models[key] = modelValue;
                    return models;
                }, {});
                this.loadingModels = false;
            }, () => {
                this.carModels = {};
                this.loadingModels = false;
            });
        },

        async submitUsedForm(e: Event) {
            e.stopPropagation();
            e.preventDefault();

            const result = await this.v$.$validate();
            if (result) {
                window.location.href = this.getUrl();
            }
        },

        getCanonicalModel(): string {
            if (!this.model) {
                return '';
            }

            return this.carModels[this.model] || this.model;
        },
        getUrl() {
            const params = new URLSearchParams();
            const canonicalModel = this.getCanonicalModel();

            if (this.make) {
                params.set('manufacturer', this.make);
            }
            if (this.range) {
                params.set('range', this.range);
            }
            if (canonicalModel) {
                params.set('model', canonicalModel);
            }
            if (this.minprice) {
                params.set('priceMin', this.minprice);
            }
            if (this.maxprice) {
                params.set('priceMax', this.maxprice);
            }

            const query = params.toString();
            return query ? `/leasing/search?${query}` : '/leasing/search';
        },
    },
    mounted() {
        const currentUrl = window.location.href;
        const isNewCars = currentUrl.includes('new-cars');
        this.searchType = isNewCars ? 'monthly' : 'cash';
    },
});

connectVue(app);
configureGlobalComponents(app);
app.mount('#app--used--landing');

